import { load } from '@iheartjane/utils';
import { Navigate, useLocation } from 'react-router';
import type { RouteObject } from 'react-router';

import { FramelessEmbedAppContainer } from '@jane/monolith/components/application/framelessEmbedAppContainer';
import { MenuSpecialsRedirect } from '@jane/shared-ecomm/pages';
import { Loader } from '@jane/shared/components';

const Checkout = Loader(
  load(() => import('@jane/monolith/components/checkout/checkout'), 'Checkout')
);
const EmbeddedCart = Loader(
  load(
    () => import('@jane/monolith/components/cart/embeddedCart'),
    'EmbeddedCart'
  )
);
const Menu = Loader(
  load(() => import('@jane/monolith/components/menu'), 'Menu'),
  'menu'
);
const MenuBrandSpecialDetail = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuBrandSpecialDetail')
);
const MenuBrandSpecials = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuBrandSpecials')
);
const MenuFeatured = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuFeatured')
);
const MenuIndex = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuIndex')
);
const MenuProductDetail = Loader(
  load(
    () => import('@jane/monolith/components/menuProductDetail/provider'),
    'MenuProductProvider'
  ),
  'menu-product-detail'
);
const MenuProducts = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuProducts')
);
const MenuSpecialDetail = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuSpecialDetail')
);
const MenuSpecialDetailRedirect = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuSpecialDetailRedirect')
);
const MenuSpecials = Loader(
  load(() => import('@jane/shared-ecomm/pages'), 'MenuSpecials')
);
const EnsureNotLoggedInContainer = Loader(
  load(
    () => import('@jane/monolith/components/ensureNotLoggedInContainer'),
    'EnsureNotLoggedInContainer'
  )
);
const ForgotPassword = Loader(
  load(
    () => import('@jane/monolith/components/login/forgotPassword'),
    'ForgotPassword'
  )
);
const Identification = Loader(
  load(
    () => import('@jane/monolith/components/profile/identification'),
    'Identification'
  )
);
const Login = Loader(
  load(() => import('@jane/monolith/components/login'), 'Login')
);
const Logout = Loader(
  load(() => import('@jane/monolith/components/logout'), 'Logout')
);
const ProductReview = Loader(
  load(
    () => import('@jane/monolith/components/productReviewForm'),
    'ProductReview'
  )
);
const Profile = Loader(
  load(() => import('@jane/monolith/components/profile'), 'Profile')
);
const ProfileChangePassword = Loader(
  load(
    () => import('@jane/monolith/components/profile/changePassword'),
    'ProfileChangePassword'
  )
);
const ProfileEdit = Loader(
  load(
    () => import('@jane/monolith/components/profile/edit/profileEdit'),
    'ProfileEdit'
  )
);
const Registration = Loader(
  load(
    () => import('@jane/monolith/components/registration/container'),
    'Registration'
  )
);
const ReplaceCart = Loader(
  load(() => import('@jane/monolith/components/replaceCart'), 'ReplaceCart')
);
const RequiresAuthentication = Loader(
  load(
    () => import('@jane/monolith/components/requiresAuthentication'),
    'RequiresAuthentication'
  )
);
const ReservationDetail = Loader(
  load(
    () => import('@jane/monolith/components/reservationDetail'),
    'ReservationDetail'
  )
);
const ReservationHistory = Loader(
  load(
    () => import('@jane/monolith/components/profile/reservationHistory'),
    'ReservationHistory'
  )
);
const ReservationReceipt = Loader(
  load(
    () => import('@jane/monolith/components/reservationReceipt'),
    'ReservationReceipt'
  )
);
const ResetPassword = Loader(
  load(() => import('@jane/monolith/components/resetPassword'), 'ResetPassword')
);

const Redirect = ({ to }: { to: string }) => {
  const location = useLocation();

  return <Navigate to={`${to}${location.search}`} replace />;
};

export const buildBoostForAllRoutes = (): RouteObject[] => {
  return [
    {
      path: '/',
      element: <FramelessEmbedAppContainer />,
      children: [
        {
          path: '/',
          element: <Redirect to="/menu" />,
        },
        {
          path: '/menu',
          element: <Menu />,
          children: [
            {
              index: true,
              element: <MenuIndex />,
            },
            {
              path: 'featured',
              element: <MenuFeatured restoreScroll />,
            },
            {
              path: ':category',
              element: <MenuProducts restoreScroll />,
            },
            {
              path: ':category/:featured_category',
              element: <MenuProducts restoreScroll />,
            },
            {
              path: 'rows/:rowId/:slug',
              element: <MenuProducts restoreScroll />,
            },
            {
              path: 'jane-gold',
              element: <MenuBrandSpecials restoreScroll />,
            },
            {
              path: 'jane-gold/:brand_special_id/:slug',
              element: <MenuBrandSpecialDetail />,
            },
            {
              path: 'specials',
              element: <MenuSpecials restoreScroll />,
            },
            {
              path: 'specials/:special_id/:slug',
              element: <MenuSpecialDetail />,
            },
          ],
        },
        {
          path: '/menu/products/:product_id/:slug',
          element: <MenuProductDetail />,
        },
        {
          path: '/products/:product_id/:slug',
          element: <MenuProductDetail />,
        },
        {
          path: '/products/:product_id/:slug/reviews/new',
          element: (
            <RequiresAuthentication>
              <ProductReview />
            </RequiresAuthentication>
          ),
        },
        {
          path: '/logout',
          element: <Logout />,
        },
        {
          path: '/cart',
          element: <EmbeddedCart />,
        },
        {
          path: '/cart/checkout',
          element: (
            <RequiresAuthentication>
              <Checkout />
            </RequiresAuthentication>
          ),
        },
        {
          path: '/cart/guest_checkout',
          element: <Checkout />,
        },
        {
          path: '/reservation_receipt/:reservation_id',
          element: <ReservationReceipt />,
        },
        {
          path: '/replaceCart',
          element: <ReplaceCart />,
        },
        {
          path: '/login',
          element: (
            <EnsureNotLoggedInContainer>
              <Login />
            </EnsureNotLoggedInContainer>
          ),
        },
        {
          path: '/register',
          element: (
            <EnsureNotLoggedInContainer>
              <Registration />
            </EnsureNotLoggedInContainer>
          ),
        },
        {
          path: '/forgot-password',
          element: <ForgotPassword />,
        },
        {
          path: '/reset-password/:token',
          element: <ResetPassword />,
        },
        {
          path: '/specials',
          element: <MenuSpecialsRedirect />,
        },
        {
          path: '/specials/:special_id',
          element: <MenuSpecialDetailRedirect />,
        },
        {
          path: '/specials/:special_id/:slug',
          element: <MenuSpecialDetailRedirect />,
        },
        {
          path: '/profile',
          element: (
            <RequiresAuthentication>
              <Profile embeddedMode />
            </RequiresAuthentication>
          ),
          children: [
            {
              path: '/profile/reservations',
              element: <ReservationHistory />,
            },
            {
              path: '/profile/reservations/:reservation_id',
              element: <ReservationDetail />,
            },
            {
              path: '/profile/identification',
              element: <Identification />,
            },
            {
              path: '/profile/edit',
              element: <ProfileEdit />,
            },
            {
              path: '/profile/password',
              element: <ProfileChangePassword />,
            },
          ],
        },
      ],
    },
  ];
};
